import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowLeft, ArrowUpRight, Loader2, TrendingUp } from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { useSession } from "@/platform/auth/use-session";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import {
  aggregateDisciplineAccuracy,
  fetchUspSessionHistory,
  fetchUspDiscursivePractice,
} from "@/tenants/usp/features/evolution/api";

export function UspEvolucaoPage() {
  const { session } = useSession();
  const query = useQuery({
    queryKey: ["usp", "session-history"],
    queryFn: () => fetchUspSessionHistory(session!.user.id),
    enabled: !!session,
  });
  const discursiveQuery = useQuery({
    queryKey: ["usp", "discursive-practice"],
    queryFn: () => fetchUspDiscursivePractice(session!.user.id),
    enabled: !!session,
  });

  const sessions = query.data ?? [];
  const discursivePractice = discursiveQuery.data ?? [];
  const disciplineAcc = aggregateDisciplineAccuracy(sessions);
  const simSessions = sessions.filter((s) => s.kind.startsWith("simulation"));
  const hasAnyData = sessions.length > 0 || discursivePractice.length > 0;

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-20 blur-2xl animate-blob" />
      <SoftStar color="mint" className="pointer-events-none absolute right-12 top-32 w-12 opacity-50 animate-float" />

      <Container size="wide" className="relative py-12 md:py-16">
        <Link to="/usp/painel" className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-foreground)]">
          <ArrowLeft className="size-3.5" />
          Painel
        </Link>

        <header className="mt-8 max-w-2xl">
          <Badge variant="accentSolid" className="gap-2">
            <TrendingUp className="size-3" />
            Evolução
          </Badge>
          <h1 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
            Seu desempenho ao longo do tempo.
          </h1>
        </header>

        {query.isLoading ? (
          <Loader2 className="mt-10 size-6 animate-spin text-[color:var(--color-muted-foreground)]" />
        ) : !hasAnyData ? (
          <div className="mt-10 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-10 text-center">
            <TrendingUp className="mx-auto size-8 text-[color:var(--color-muted-foreground)]" />
            <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
              Nenhum simulado finalizado ainda.
            </p>
            <Button asChild className="mt-5" variant="primary">
              <Link to="/usp/1afase">
                Fazer 1º simulado
                <ArrowUpRight className="size-4" />
              </Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Histórico de simulados */}
            <section className="mt-10">
              <h2 className="font-display text-[length:var(--text-body-lg)] font-semibold mb-4">
                Histórico de simulados ({simSessions.length})
              </h2>
              <div className="overflow-x-auto rounded-2xl border border-[--color-border]">
                <table className="w-full text-[length:var(--text-body-sm)]">
                  <thead className="bg-[color:var(--color-muted)] text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Data</th>
                      <th className="px-4 py-3 text-left font-medium">Tipo</th>
                      <th className="px-4 py-3 text-right font-medium">Acertos</th>
                      <th className="px-4 py-3 text-right font-medium">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {simSessions.map((s) => {
                      const pct = s.total_questions > 0 ? Math.round((s.correct_count / s.total_questions) * 100) : 0;
                      return (
                        <tr key={s.id} className="border-t border-[--color-border]">
                          <td className="px-4 py-3 text-[color:var(--color-muted-foreground)]">
                            {s.finished_at ? format(parseISO(s.finished_at), "d MMM yyyy", { locale: ptBR }) : "—"}
                          </td>
                          <td className="px-4 py-3 capitalize">{s.kind.replace("simulation_", "").replace("_", " ")}</td>
                          <td data-tabular="true" className="px-4 py-3 text-right">{s.correct_count}/{s.total_questions}</td>
                          <td data-tabular="true" className="px-4 py-3 text-right font-medium" style={{ color: "var(--color-accent-deep)" }}>{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Acerto por disciplina (acumulado) */}
            <section className="mt-10">
              <h2 className="font-display text-[length:var(--text-body-lg)] font-semibold mb-4">
                Acerto por disciplina (acumulado)
              </h2>
              <ul className="space-y-2">
                {disciplineAcc.map((d) => (
                  <li key={d.discipline} className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-[--color-border] bg-[color:var(--color-card)] p-4">
                    <div className="flex items-center gap-3">
                      <span className="w-32 text-[length:var(--text-body-sm)] font-medium capitalize">{d.discipline}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-[color:var(--color-muted)]">
                        <div className="h-full" style={{ width: `${d.pct}%`, backgroundColor: d.pct >= 70 ? "var(--color-success)" : d.pct >= 40 ? "var(--color-warning)" : "var(--color-danger)" }} />
                      </div>
                    </div>
                    <span data-tabular="true" className="text-[length:var(--text-body-sm)] font-medium tabular-nums">
                      {d.correct}/{d.total} ({d.pct}%)
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Prática de discursivas / redação (2ª fase) */}
            {discursivePractice.length > 0 && (
              <section className="mt-10">
                <h2 className="font-display text-[length:var(--text-body-lg)] font-semibold mb-4">
                  Discursivas treinadas (2ª fase)
                </h2>
                <ul className="space-y-2">
                  {discursivePractice.map((d) => (
                    <li
                      key={d.discipline}
                      className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-[--color-border] bg-[color:var(--color-card)] p-4"
                    >
                      <span className="text-[length:var(--text-body-sm)] font-medium capitalize">
                        {d.discipline}
                      </span>
                      <span className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                        {d.attempts} {d.attempts === 1 ? "correção" : "correções"} · média{" "}
                        <span className="font-medium text-[color:var(--color-foreground)] tabular-nums">
                          {d.avgScore.toLocaleString("pt-BR")} pts
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </Container>
    </div>
  );
}
